/**
 * Buyer listing visibility — existing DB fields only (no schema changes).
 * Upcoming projects are separate; regular listings hide only on manual Hide
 * (is_active = 0 while visibility_end_date is still in the future).
 */

export const isUpcomingProject = (property) => {
  const projectType = String(property?.project_type ?? property?.projectType ?? '').toLowerCase();
  return projectType === 'upcoming';
};

export const isUpcomingProjectRequest = (params = {}) =>
  String(params.project_type || params.projectType || '').toLowerCase() === 'upcoming';

const parseVisibilityEnd = (property) => {
  const raw = property?.visibility_end_date ?? property?.visibilityEndDate;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Matches backend property_visibility.php logic. */
export const isRegularListingVisibleToBuyers = (property) => {
  if (!property || typeof property !== 'object') return false;
  if (isUpcomingProject(property)) return true;

  const active = property.is_active ?? property.isActive;
  if (active === true || active === 1 || active === '1') return true;

  const endDate = parseVisibilityEnd(property);
  if (!endDate) return true;

  return endDate.getTime() < Date.now();
};

export const filterRegularBuyerProperties = (properties) => {
  if (!Array.isArray(properties)) return [];
  return properties.filter(isRegularListingVisibleToBuyers);
};

export const adjustPaginationAfterFilter = (pagination, removedCount, fallbackLength = 0) => {
  if (!pagination || removedCount <= 0) return pagination;

  const limit = Number(pagination.limit) || fallbackLength || 1;
  const nextTotal = Math.max(0, (Number(pagination.total) || fallbackLength) - removedCount);

  return {
    ...pagination,
    total: nextTotal,
    total_pages: Math.max(1, Math.ceil(nextTotal / limit)),
  };
};

/** Normalize listing status to API key: sale | rent | '' */
export const normalizeListingStatusKey = (value) => {
  const normalized = String(value || '').toLowerCase().replace(/\s+/g, '');
  if (normalized.includes('rent')) return 'rent';
  if (normalized.includes('sale') || normalized === 'buy') return 'sale';
  return '';
};

export const formatListingStatusLabel = (statusKey) => {
  if (statusKey === 'rent') return 'For Rent';
  if (statusKey === 'sale') return 'For Sale';
  return '';
};

export const resolveListingStatusFromSearchParams = (searchParams) => {
  if (!searchParams) return '';

  let status = searchParams.get('status') || '';
  const propertyType = (searchParams.get('propertyType') || '').toLowerCase();

  if (propertyType === 'buy') status = status || 'sale';
  else if (propertyType === 'rent') status = status || 'rent';

  return normalizeListingStatusKey(status);
};

/** Seller/agent dashboard Hide/Show toggle state. */
export const isSellerToggleVisible = (property) => {
  const active = property?.isActive ?? property?.is_active;
  return active === true || active === 1 || active === '1';
};
